"use client";
import AboutUsSection from "../components/home/about-us/AboutUsSection";
import CallToActionSection from "../components/home/call-to-action/CallToActionSection";
import CollaboratedCompanySection from "../components/home/collaborated-company/CollaboratedCompanySection";
import EventSection from "@/components/home/event/EventSection";
import UpcomingAdmissionsSection from "@/components/home/upcoming-admissions/UpcomingAdmissionsSection";
import FacultySection from "../components/home/faculty/FacultySection";
import HeroSection from "../components/home/hero/HeroSection";
import LeadFormSection from "../components/home/lead-form/LeadFormSection";
import FreeCourses from "../components/home/free-courses/FreeCourses";
import Internship from "@/components/home/internship/Internship";
import WhatsAppButton from "@/components/common/WhatsAppButton";
import { useRouter } from "next/navigation";
import { useState, useEffect } from "react";
import { prodServerUrl } from "@/app/utils/globals";
import { IoClose } from "react-icons/io5";
import { Review } from "@/components/home/reviews/Review";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

export default function Page() {
  const [showPopup, setShowPopup] = useState(false);
  const [disable, setDisable] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    selectedCourse: "",
  });
  
  const router = useRouter();

  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowPopup(true);
    }, 5000);
    return () => clearTimeout(timeout);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.selectedCourse) {
      alert("All Fields Are Necessary");
      return;
    }

    try {
      setDisable(true);
      const res = await axios.post(`${prodServerUrl}/hero-forms`, formData);

      if (res.status === 201) {
        alert("We have received your query. You will be contacted soon");
        setFormData({ name: "", phone: "", selectedCourse: "" });
        setShowPopup(false);
        router.push("/thank-you");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setDisable(false);
    }
  };

  return (
    <>
      <div>
        <HeroSection />
        <AboutUsSection />
        <EventSection />
        {/* <UpcomingAdmissionsSection /> */}
        <CollaboratedCompanySection />
        <LeadFormSection />
        <FreeCourses />
        <FacultySection />
        <CallToActionSection />
        <Review />
        <Internship />
        <WhatsAppButton />
      </div>

      <AnimatePresence>
        {showPopup && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative w-full max-w-lg"
            >
              {/* Close Button */}
              <button
                onClick={() => setShowPopup(false)}
                className="absolute -top-4 -right-4 z-50 p-2 bg-white rounded-full shadow-lg text-gray-600 hover:text-red-500 hover:bg-gray-100 transition-colors"
              >
                <IoClose size={24} />
              </button>

              <div className="relative overflow-hidden bg-slate-900 shadow-2xl border border-white/10">
                
                {/* Decorative Background Glows */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/20 blur-[80px] pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-600/20 blur-[80px] pointer-events-none" />

                <div className="relative z-10 p-8 md:p-10">
                  
                  {/* Header */}
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-white mb-2 tracking-tight">
                      Unlock Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">Future</span>
                    </h2>
                    <p className="text-slate-400 text-sm">
                      Get expert guidance & syllabus details instantly.
                    </p>
                  </div>

                  {/* Highlight Box */}
                  <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 p-4 mb-8 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/20 text-red-400 text-xs font-bold uppercase tracking-wider mb-2 animate-pulse">
                      Admissions Open
                    </div>
                    <p className="text-blue-100 text-sm font-medium leading-relaxed">
                      Content Writing, Digital Marketing & Full Stack Web Development
                    </p>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="space-y-5">
                    
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-slate-400 ml-1 uppercase">Select Course</label>
                      <select
                        name="selectedCourse"
                        value={formData.selectedCourse}
                        onChange={handleChange}
                        className="w-full bg-slate-800/50 border border-slate-700 px-4 py-3.5 text-white placeholder-slate-500 focus:outline-none appearance-none cursor-pointer hover:bg-slate-800"
                        required
                      >
                        <option value="" className="text-slate-500">Choose your path...</option>
                        <option value="content writing">Content Writing</option>
                        <option value="video editing">Basic Video Editing & Graphic Design</option>
                        <option value="digital marketing">Digital Marketing</option>
                        <option value="ai & ml">AI & Machine Learning</option>
                        <option value="ui ux">UI/UX Design</option>
                        <option value="data science">Data Science & Analytics</option>
                        <option value="web development">Full Stack Web Development</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="text-xs font-semibold text-slate-400 ml-1 uppercase">Full Name</label>
                        <input
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          type="text"
                          className="w-full bg-slate-800/50 border border-slate-700 px-4 py-3.5 text-white placeholder-slate-600 focus:outline-none"
                          placeholder="John Doe"
                          required
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-xs font-semibold text-slate-400 ml-1 uppercase">Phone Number</label>
                        <input
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full bg-slate-800/50 border border-slate-700 px-4 py-3.5 text-white placeholder-slate-600 focus:outline-none"
                          placeholder="+91 98765 43210"
                          required
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={disable}
                      className="w-full mt-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-bold py-4 shadow-lg shadow-blue-500/25 transform transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {disable ? "Submitting..." : "Get Free Counseling"}
                    </button>

                    <p className="text-center text-[10px] text-slate-500 mt-4">
                      By submitting, you agree to our Terms & Privacy Policy.
                    </p>
                  </form>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}